/**
 * Step-by-step cancellation guides on /how-to-cancel/<slug>, one per
 * competitor in constants/competitors.ts (same slugs).
 *
 * Built for "cancel <name> subscription" searches (DataForSEO, 2026-10-21:
 * MyPerfectResume 480 a month, Resume Genius 390, Resume.io 320): people who
 * are leaving a renewing plan, which is the case for a one-time price. The
 * /alternatives pages answer it in one card and do not rank for it.
 *
 * The rules of constants/competitors.ts apply: every line is the competitor's
 * own wording turned into steps, with the URL it was read from, and `checked`
 * is the date. No adjectives about their product. English only, like the
 * searches; the page has no /cs or /de copy.
 */
import type { SourcedFact } from '~/constants/competitors'

export interface CancelMethod {
  title: string
  steps: string[]
  source: string
}

export interface CancelGuide {
  slug: string
  /** ISO date the steps were read. */
  checked: string
  seoTitle: string
  seoDescription: string
  /** The whole answer in two or three sentences, for the box at the top. */
  short: string
  methods: CancelMethod[]
  /** When to cancel so the trial does not turn into a renewing plan. */
  timing: SourcedFact
  refund?: SourcedFact
  /** What happens to access and documents afterwards. */
  after: SourcedFact
}

export const CANCEL_GUIDES: CancelGuide[] = [
  {
    slug: 'myperfectresume',
    // Its site blocks automated reading; these are the terms and pricing as
    // read on 2026-09-29, and the contact line as shown on its own page in
    // Google results on 2026-10-21. Recheck in a browser.
    checked: '2026-09-29',
    seoTitle: 'How to Cancel MyPerfectResume: Steps, Refund and Contact (2026)',
    seoDescription: 'Cancel MyPerfectResume online under your account settings or through customer service, before the 14-day trial renews at $23.95 every 4 weeks. Refunds, and what to download first.',
    short: 'Cancel MyPerfectResume on the online cancel page under your account settings, or by contacting customer service; either way you get a cancellation number by e-mail. Do it within the 14 days of the $2.95 trial, or it renews at $23.95 every 4 weeks. The pricing page offers a refund if you ask within 14 days of subscribing.',
    methods: [
      {
        title: 'Online, in your account',
        steps: [
          'Log in to your MyPerfectResume account.',
          'Open your account settings and go to the online cancel page.',
          'Confirm the cancellation, then keep the cancellation number that arrives by e-mail.',
        ],
        source: 'https://www.myperfectresume.com/terms-conditions',
      },
      {
        title: 'Through customer service',
        steps: [
          'Call (844) 485-4051 or e-mail customerservice@myperfectresume.com.',
          'Ask to cancel your subscription and give the e-mail address on the account.',
          'Keep the cancellation number they send you as proof.',
        ],
        source: 'https://www.myperfectresume.com/accessibility',
      },
    ],
    timing: {
      text: 'The $2.95 premium access lasts 14 days and then auto-renews at $23.95 billed every 4 weeks. The yearly plan ($95.40) renews each year. Cancel before the renewal date to avoid the next charge.',
      source: 'https://www.myperfectresume.com/pricing',
    },
    refund: {
      text: 'The pricing page offers a refund if you ask within 14 days of subscribing.',
      source: 'https://www.myperfectresume.com/pricing',
    },
    after: {
      text: 'The free plan downloads TXT only; PDF and Word downloads are premium. Download any PDF or Word copy you need while premium is still active.',
      source: 'https://www.myperfectresume.com/pricing',
    },
  },
  {
    slug: 'resume-genius',
    checked: '2026-10-21',
    seoTitle: 'How to Cancel Resume Genius: Steps, Refund and Phone (2026)',
    seoDescription: 'Cancel Resume Genius in My Account, My Subscription, Cancel Subscription, or by phone or e-mail, before the $2.95 trial renews at $23.95 every four weeks. The 14-day refund, and what to download first.',
    short: 'Log in to Resume Genius, open My Account, then My Subscription, and click Cancel Subscription; you can also cancel by phone at (866) 215-9048 or by e-mail. Cancel within 14 days of the $2.95 trial, or it renews at $23.95 every four weeks. The pricing page promises a full refund if you ask within 14 days.',
    methods: [
      {
        title: 'In your account',
        steps: [
          'Log in to your Resume Genius account.',
          'Select the profile icon in the upper-left corner and click My Account.',
          'Select My Subscription at the top of the window.',
          'Click Cancel Subscription and follow the instructions.',
        ],
        source: 'https://resumegenius.com/faq/how-do-i-cancel-my-resume-genius-subscription',
      },
      {
        title: 'By phone or e-mail',
        steps: [
          'Call (866) 215-9048 (US, toll-free) or e-mail support@resumegenius.com.',
          'Ask to cancel and give the e-mail address on the account.',
        ],
        source: 'https://resumegenius.com/pricing',
      },
    ],
    timing: {
      text: 'The $2.95 trial auto-renews after 14 days at $23.95 billed every four weeks. The annual plan ($95.40) auto-renews each year. Cancel before the renewal date to avoid the next charge.',
      source: 'https://resumegenius.com/pricing',
    },
    refund: {
      text: 'The pricing page offers a 14-day money-back guarantee: let them know within 14 days and they process a full refund.',
      source: 'https://resumegenius.com/pricing',
    },
    after: {
      text: 'The free plan downloads resumes as TXT only. Download any PDF or Word copy you need before the paid plan ends.',
      source: 'https://resumegenius.com/pricing',
    },
  },
  {
    slug: 'resume-io',
    checked: '2026-10-21',
    seoTitle: 'How to Cancel Resume.io: Steps, Refund and What You Keep (2026)',
    seoDescription: 'Cancel Resume.io from the Cancel Subscription tab on its contact page or in Account Settings, before the 7-day trial renews at $29.95 every 4 weeks. The 7-day money-back guarantee, and what happens to your resumes.',
    short: 'Go to the Cancel Subscription tab on Resume.io\'s contact page, find your account by e-mail and cancel, then confirm with the link in the e-mail it sends; logged in, the downgrade option is at the top of Account Settings. Cancel within 7 days of the $2.95 trial, or you are subscribed at $29.95 every 4 weeks. The pricing page offers a 7-day money-back guarantee.',
    methods: [
      {
        title: 'On the website, without logging in',
        steps: [
          'Open resume.io/contact and choose the Cancel Subscription tab.',
          'Click "Cancel your subscription" and search for your account by e-mail.',
          'Select the cancel button on your account.',
          'Confirm with the link in the confirmation e-mail. If it does not arrive, check spam or search your inbox for "resume.io".',
        ],
        source: 'https://help.resume.io/en/articles/3784896',
      },
      {
        title: 'In the app',
        steps: [
          'Log in, click your profile photo in the upper right and choose Account Settings.',
          'Use the downgrade option at the top of the page.',
          'If your account is not found or no confirmation e-mail arrives, write to support@resume.io.',
        ],
        source: 'https://help.resume.io/en/articles/3784896',
      },
    ],
    timing: {
      text: 'After the 7-day trial, if you do not cancel, you are automatically subscribed and billed $29.95 every 4 weeks. The quarterly plan ($49.95) renews every 3 months.',
      source: 'https://resume.io/pricing',
    },
    refund: {
      text: 'The pricing page lists a 7-day money-back guarantee on the trial and the quarterly plan.',
      source: 'https://resume.io/pricing',
    },
    after: {
      text: 'The help article says premium lasts 30 days from your last monthly payment (7 days from a trial payment), and that your resumes and cover letters remain available to download after the downgrade; the pricing page lists free downloads as TXT only. Deleting the account under Danger Zone is permanent.',
      source: 'https://help.resume.io/en/articles/3784896',
    },
  },
  {
    slug: 'livecareer',
    checked: '2026-10-21',
    seoTitle: 'How to Cancel LiveCareer: Online, Phone and Refund (2026)',
    seoDescription: 'Cancel LiveCareer online under your account settings, by e-mail or toll-free at 800-652-8430, before the 14-day trial renews at $23.95 every four weeks. The 14-day refund, and what to download first.',
    short: 'Cancel LiveCareer online on the cancel page under your account settings, by e-mail, or by calling 800-652-8430 toll-free. Do it within the 14 days of the $2.95 trial, or it renews at $23.95 every four weeks. The pricing page offers a refund within those 14 days.',
    methods: [
      {
        title: 'Online, in your account',
        steps: [
          'Log in to your LiveCareer account.',
          'Open your account settings and go to the online cancel page.',
          'Confirm the cancellation and keep the confirmation number sent by e-mail.',
        ],
        // The terms (read 2026-09-29, now bot-blocked) describe the cancel page
        // and the e-mailed number; the pricing page says "cancel any time online".
        source: 'https://www.livecareer.com/pricing',
      },
      {
        title: 'By phone or e-mail',
        steps: [
          'Call customer support toll-free at 800-652-8430, or e-mail them to start the cancellation.',
          'Give the e-mail address on the account and keep the confirmation you receive.',
        ],
        source: 'https://www.livecareer.com/pricing',
      },
    ],
    timing: {
      text: 'The $2.95 premium access lasts 14 days and then auto-renews at $23.95 billed every four weeks. The annual plan ($71.40) renews each year.',
      source: 'https://www.livecareer.com/pricing',
    },
    refund: {
      text: 'The pricing page offers a money-back guarantee within the 14 days of the trial.',
      source: 'https://www.livecareer.com/pricing',
    },
    after: {
      text: 'The free plan downloads resumes as TXT only; PDF, Word and RTF are premium. Download the copies you need before premium ends.',
      source: 'https://www.livecareer.com/pricing',
    },
  },
  {
    slug: 'enhancv',
    checked: '2026-10-21',
    seoTitle: 'How to Cancel Enhancv: Billing Page Steps and What You Keep (2026)',
    seoDescription: 'Cancel Enhancv on the Billing page: Cancel Subscription, a short survey, confirm. Pro stays until the end of the paid period, then the account is read-only and your documents stay downloadable.',
    short: 'Open the Billing page in Enhancv, click Cancel Subscription, answer the short survey and confirm; the page then shows the plan as Canceled with its expiry date. Pro access lasts until the end of the period you paid for, after which the account is read-only but your existing documents can still be downloaded.',
    methods: [
      {
        title: 'On the Billing page',
        steps: [
          'Go to your Billing page (app.enhancv.com/billing).',
          'Click Cancel Subscription.',
          'Answer the survey and click Continue.',
          'Confirm the cancellation.',
          'Check that the Billing page shows the subscription as Canceled, with the date your Pro access ends.',
        ],
        source: 'https://help.enhancv.com/en/articles/2794038-how-do-i-cancel-my-plan-and-stop-future-charges',
      },
    ],
    timing: {
      text: 'Every Pro plan ($39 a month, $69 every 3 months, $99 every 6 months) renews automatically until you cancel, and the terms make cancelling before the next billing date your responsibility.',
      source: 'https://enhancv.com/terms/',
    },
    after: {
      text: 'You keep full Pro access to edit and download until the paid period ends. After that the account is read-only: existing documents stay available to download, but you cannot save edits or create new ones. Cancelling does not unsubscribe you from the newsletter; change that in your Profile settings.',
      source: 'https://help.enhancv.com/en/articles/2794038-how-do-i-cancel-my-plan-and-stop-future-charges',
    },
  },
  {
    slug: 'resume-worded',
    checked: '2026-10-21',
    seoTitle: 'How to Cancel Resume Worded: Steps and Refunds (2026)',
    seoDescription: 'Cancel Resume Worded in My Account, Account and Subscription Details, Cancel Subscription. Pro stays active until the renewal date; what the terms say about refunds.',
    short: 'In Resume Worded, go to My Account, then Account and Subscription Details, choose Cancel Subscription and confirm with the green button; you will not be billed again. Pro stays active until the date it would have renewed.',
    methods: [
      {
        title: 'In your account',
        steps: [
          'Go to My Account.',
          'Open Account and Subscription Details.',
          'Choose Cancel Subscription.',
          'Click the green Cancel Subscription button to confirm.',
        ],
        source: 'https://help.resumeworded.com/article/15-how-do-i-cancel-my-subscription',
      },
    ],
    timing: {
      text: 'Pro Monthly ($49) is billed every calendar month and Pro Quarterly ($99) every 3 months until you cancel. The one-year option ($229) is a single payment.',
      source: 'https://resumeworded.com/get-pro',
    },
    refund: {
      text: 'The terms say refunds are not given to first-time buyers.',
      source: 'https://resumeworded.com/terms',
    },
    after: {
      text: 'If you cancel before the renewal date, you keep every Pro feature until that date.',
      source: 'https://help.resumeworded.com/article/15-how-do-i-cancel-my-subscription',
    },
  },
]

export const CANCEL_GUIDE_SLUGS = CANCEL_GUIDES.map(g => g.slug)

export function findCancelGuide(slug: string): CancelGuide | undefined {
  return CANCEL_GUIDES.find(g => g.slug === slug)
}
